import { useState, useEffect } from "react";
import {
  Search,
  Users,
  Building2,
  Loader2,
  CreditCard,
  MoreVertical,
  UserCheck,
  UserX,
  Edit2,
  Trash2,
  AlertTriangle,
  Phone,
  MessageCircle,
  StickyNote,
  CheckCircle2,
  LogIn,
} from "lucide-react";

/**
 * Normaliza un teléfono a número internacional para un enlace wa.me (solo
 * dígitos, sin `+`). Devuelve null si no parece un teléfono válido, para NO
 * pintar el botón de WhatsApp en esos casos (p. ej. un email colado en el
 * campo, o un número demasiado corto). Producto de mercado español: un número
 * nacional de 9 dígitos se asume España (+34).
 */
function toWhatsAppNumber(raw: string | null | undefined): string | null {
  if (!raw) return null;
  const hadPlus = raw.trim().startsWith("+");
  const digits = raw.replace(/\D/g, "");
  if (digits.length < 8) return null; // basura o demasiado corto (email → 0 dígitos)
  if (hadPlus) return digits; // internacional explícito (+34…, +54…)
  if (digits.startsWith("34") && digits.length === 11) return digits; // ES con prefijo sin +
  if (digits.length === 9) return `34${digits}`; // móvil/fijo nacional ES
  return digits; // resto: mejor esfuerzo (número extranjero sin +)
}
import apiClient from "@/lib/api-client";
import Button from "@/components/Button";
import { useLocation } from "@/contexts/LocationContext";
import { useConfirm } from "@/contexts/ConfirmContext";
import { startImpersonation } from "@/hooks/useImpersonation";
import { useGlobalToast } from "../../contexts/ToastContext";

function extractErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === "object" && "response" in err) {
    const response = (
      err as { response?: { data?: { error?: string; message?: string } } }
    ).response;
    return response?.data?.error || response?.data?.message || fallback;
  }
  return fallback;
}

interface Plan {
  id: string;
  name: string;
  price: number;
}

interface User {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  role: string;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
  adminNotes: string | null;
  organization: {
    id: string;
    name: string;
    slug: string;
    subscriptionStatus: string;
    plan: Plan | null;
    /** Por dónde nos encontró. Null si no aceptó cookies de marketing. */
    adClickAttribution: {
      gclid: string | null;
      gbraid: string | null;
      wbraid: string | null;
      fbclid: string | null;
      utmSource: string | null;
      utmMedium: string | null;
      utmCampaign: string | null;
      referrer: string | null;
      landingPage: string | null;
      keywordText: string | null;
    } | null;
  };
}

/**
 * "¿Por dónde nos encontró?" en cristiano. El orden importa: primero lo que
 * PAGAMOS (un clic de anuncio manda sobre cualquier otra cosa), después la
 * campaña etiquetada, después de dónde venía, y por último la página de
 * entrada. Sin consentimiento de marketing no hay ficha: "Sin datos" es la
 * respuesta honesta, no un fallo.
 */
function origenDelRegistro(
  attr: User["organization"]["adClickAttribution"],
): { texto: string; detalle?: string } {
  if (!attr) return { texto: "Sin datos" };
  if (attr.gclid || attr.gbraid || attr.wbraid) {
    return {
      texto: "Anuncio de Google",
      detalle: attr.keywordText ? `buscó "${attr.keywordText}"` : attr.utmCampaign ?? undefined,
    };
  }
  if (attr.fbclid) {
    return { texto: "Anuncio de Meta", detalle: attr.utmCampaign ?? undefined };
  }
  if (attr.utmSource) {
    const medio = attr.utmMedium ? ` (${attr.utmMedium})` : "";
    return { texto: `${attr.utmSource}${medio}`, detalle: attr.utmCampaign ?? undefined };
  }
  if (attr.referrer) {
    const bonitos: Record<string, string> = {
      "google.com": "Búsqueda en Google",
      "google.es": "Búsqueda en Google",
      "bing.com": "Búsqueda en Bing",
      "instagram.com": "Instagram",
      "l.instagram.com": "Instagram",
      "facebook.com": "Facebook",
      "m.facebook.com": "Facebook",
      "linkedin.com": "LinkedIn",
      "t.co": "X (Twitter)",
      "chatgpt.com": "ChatGPT",
    };
    return {
      texto: bonitos[attr.referrer] ?? attr.referrer,
      detalle: attr.landingPage ? `entró por ${new URL(attr.landingPage).pathname}` : undefined,
    };
  }
  if (attr.landingPage) {
    try {
      return { texto: "Directo", detalle: `entró por ${new URL(attr.landingPage).pathname}` };
    } catch {
      return { texto: "Directo" };
    }
  }
  return { texto: "Sin datos" };
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export default function UsersAdmin() {
  const { refreshLocations } = useLocation();
  const { showToast } = useGlobalToast();
  const { confirm } = useConfirm();
  const [users, setUsers] = useState<User[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [planFilter, setPlanFilter] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({
    firstName: "",
    lastName: "",
    role: "",
    planId: "",
    adminNotes: "",
  });
  const [isUpdating, setIsUpdating] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [userToDelete, setUserToDelete] = useState<User | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [notesUser, setNotesUser] = useState<User | null>(null);
  const [notesText, setNotesText] = useState("");
  const [isSavingNotes, setIsSavingNotes] = useState(false);
  const [impersonatingUserId, setImpersonatingUserId] = useState<
    string | null
  >(null);

  useEffect(() => {
    fetchPlans();
  }, []);

  useEffect(() => {
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, roleFilter, planFilter]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (pagination.page === 1) {
        fetchUsers();
      } else {
        setPagination((prev) => ({ ...prev, page: 1 }));
      }
    }, 300);
    return () => clearTimeout(delayDebounce);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  const fetchPlans = async () => {
    try {
      const response = await apiClient.get("/admin/plans");
      setPlans(response.data.data);
    } catch (err) {
      console.error("Error al cargar planes:", err);
    }
  };

  const fetchUsers = async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
      });
      if (searchTerm) params.append("search", searchTerm);
      if (roleFilter) params.append("role", roleFilter);
      if (planFilter) params.append("planId", planFilter);

      const response = await apiClient.get(`/admin/users?${params}`);
      setUsers(response.data.data);
      setPagination(response.data.pagination);
    } catch (err) {
      showToast("Error al cargar usuarios", "error");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleStatus = async (user: User) => {
    try {
      setIsUpdating(true);
      const response = await apiClient.patch(`/admin/users/${user.id}/status`);
      showToast(response.data.message, "success");
      fetchUsers();
      // Refresh location context: orgs without active admins must disappear from switcher
      refreshLocations();
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error && "response" in err
          ? (err as { response?: { data?: { message?: string } } }).response
              ?.data?.message
          : "Error al cambiar estado del usuario";
      showToast(errorMessage || "Error al cambiar estado del usuario", "error");
    } finally {
      setIsUpdating(false);
      setActiveDropdown(null);
    }
  };

  const handleImpersonate = async (user: User) => {
    const confirmed = await confirm({
      title: `¿Acceder como ${user.firstName} ${user.lastName}?`,
      message: `Entrarás al dashboard con los permisos de este usuario, viendo la organización "${user.organization.name}" tal y como la ve él/ella. Podrás volver a tu cuenta de superadmin en cualquier momento desde el aviso que aparecerá arriba.`,
      confirmText: "Sí, acceder como este usuario",
    });
    if (!confirmed) return;

    try {
      setImpersonatingUserId(user.id);
      const response = await apiClient.post(`/admin/impersonate/${user.id}`);
      const { token } = response.data;
      const targetLabel = `${user.firstName} ${user.lastName} (${user.organization.name})`;
      // Redirige y recarga la app con el token de impersonación activo.
      startImpersonation(token, targetLabel);
    } catch (err: unknown) {
      showToast(
        extractErrorMessage(err, "Error al acceder como este usuario"),
        "error",
      );
      setImpersonatingUserId(null);
    }
  };

  const openEditModal = (user: User) => {
    setSelectedUser(user);
    setEditFormData({
      firstName: user.firstName,
      lastName: user.lastName,
      role: user.role,
      planId: user.organization.plan?.id || "",
      adminNotes: user.adminNotes || "",
    });
    setShowEditModal(true);
    setActiveDropdown(null);
  };

  const openNotesModal = (user: User) => {
    setNotesUser(user);
    setNotesText(user.adminNotes || "");
    setShowNotesModal(true);
    setActiveDropdown(null);
  };

  const handleSaveNotes = async () => {
    if (!notesUser) return;
    try {
      setIsSavingNotes(true);
      await apiClient.put(`/admin/users/${notesUser.id}`, {
        adminNotes: notesText || null,
      });
      showToast("Anotación guardada correctamente", "success");
      setShowNotesModal(false);
      setNotesUser(null);
      fetchUsers();
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error && "response" in err
          ? (err as { response?: { data?: { message?: string } } }).response
              ?.data?.message
          : "Error al guardar la anotación";
      showToast(errorMessage || "Error al guardar la anotación", "error");
    } finally {
      setIsSavingNotes(false);
    }
  };

  const openDeleteModal = (user: User) => {
    setUserToDelete(user);
    setShowDeleteModal(true);
    setActiveDropdown(null);
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    try {
      setIsDeleting(true);
      const response = await apiClient.delete(
        `/admin/users/${userToDelete.id}`,
      );
      showToast(response.data.message, "success");
      setShowDeleteModal(false);
      setUserToDelete(null);
      fetchUsers();
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error && "response" in err
          ? (err as { response?: { data?: { message?: string } } }).response
              ?.data?.message
          : "Error al eliminar usuario";
      showToast(errorMessage || "Error al eliminar usuario", "error");
    } finally {
      setIsDeleting(false);
    }
  };

  const handleUpdateUser = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUser) return;

    try {
      setIsUpdating(true);
      await apiClient.put(`/admin/users/${selectedUser.id}`, {
        ...editFormData,
        adminNotes: editFormData.adminNotes || null,
      });
      showToast("Usuario actualizado correctamente", "success");
      setShowEditModal(false);
      setSelectedUser(null);
      fetchUsers();
    } catch (err: unknown) {
      const errorMessage =
        err instanceof Error && "response" in err
          ? (err as { response?: { data?: { message?: string } } }).response
              ?.data?.message
          : "Error al actualizar usuario";
      showToast(errorMessage || "Error al actualizar usuario", "error");
    } finally {
      setIsUpdating(false);
    }
  };

  const getRoleBadge = (role: string) => {
    const styles: Record<string, string> = {
      owner: "bg-purple-100 text-purple-800",
      admin: "bg-blue-100 text-blue-800",
      staff: "bg-gray-100 text-gray-800",
    };
    const labels: Record<string, string> = {
      owner: "Superadmin",
      admin: "Administrador",
      staff: "Staff",
    };
    return (
      <span
        className={`px-2 py-1 text-xs font-medium rounded-full ${styles[role] || styles.staff}`}
      >
        {labels[role] || role}
      </span>
    );
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return "Nunca";
    return new Date(dateString).toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 mb-3">
        <h1 className="text-xl md:text-2xl font-bold text-gray-900">
          Usuarios
        </h1>
        <p className="text-gray-500 text-sm mt-0.5">
          Gestiona todos los usuarios de la plataforma
        </p>
      </div>

      {/* Card principal — ocupa todo el espacio restante */}
      <div className="flex-1 min-h-0 flex flex-col overflow-hidden bg-white rounded-xl shadow-sm border border-gray-200">
        {/* Filtros */}
        <div className="flex-shrink-0 px-4 py-3 border-b border-gray-200 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search
              className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400"
              size={18}
            />
            <input
              type="text"
              placeholder="Buscar por nombre o email..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-9 pr-4 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          <select
            value={roleFilter}
            onChange={(e) => setRoleFilter(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">Todos los roles</option>
            <option value="owner">Superadmin</option>
            <option value="admin">Administrador</option>
            <option value="staff">Staff</option>
          </select>
          <select
            value={planFilter}
            onChange={(e) => setPlanFilter(e.target.value)}
            className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            <option value="">Todos los planes</option>
            {plans.map((plan) => (
              <option key={plan.id} value={plan.id}>
                {plan.name} {plan.price > 0 ? `(${plan.price}€)` : "(Gratis)"}
              </option>
            ))}
          </select>
        </div>

        {isLoading ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
          </div>
        ) : (
          <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
            {/* Tabla con scroll propio */}
            <div className="flex-1 overflow-y-auto overflow-x-auto">
              <table className="w-full min-w-[1000px]">
                <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Usuario
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Organización
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Plan
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Rol
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Estado
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Notas
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Nos encontró por
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Registro
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Último acceso
                    </th>
                    <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Acciones
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {users.length === 0 ? (
                    <tr>
                      <td
                        colSpan={9}
                        className="px-4 py-12 text-center text-gray-500"
                      >
                        No se encontraron usuarios
                      </td>
                    </tr>
                  ) : (
                    users.map((user) => (
                      <tr key={user.id} className="hover:bg-gray-50">
                        <td className="px-4 py-2.5">
                          <div className="flex items-center">
                            <div className="h-8 w-8 flex-shrink-0 bg-primary-100 rounded-full flex items-center justify-center">
                              <Users className="h-4 w-4 text-primary-600" />
                            </div>
                            <div className="ml-3">
                              <div className="text-sm font-medium text-gray-900">
                                {user.firstName} {user.lastName}
                              </div>
                              <div className="text-xs text-gray-500">
                                {user.email}
                              </div>
                              {user.phone && (
                                <div className="flex items-center gap-2 mt-0.5">
                                  <a
                                    href={`tel:${user.phone}`}
                                    className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-800"
                                  >
                                    <Phone className="h-3 w-3" />
                                    {user.phone}
                                  </a>
                                  {toWhatsAppNumber(user.phone) && (
                                    <a
                                      href={`https://wa.me/${toWhatsAppNumber(user.phone)}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      title="Abrir chat de WhatsApp"
                                      className="flex items-center gap-1 text-xs text-green-600 hover:text-green-700"
                                    >
                                      <MessageCircle className="h-3 w-3" />
                                      WhatsApp
                                    </a>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-2.5">
                          <div className="flex items-center text-sm text-gray-900">
                            <Building2 className="h-4 w-4 mr-1.5 text-gray-400 flex-shrink-0" />
                            <span className="truncate max-w-[130px]">
                              {user.organization.name}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-2.5">
                          <div className="flex items-center gap-1.5">
                            <CreditCard className="h-4 w-4 text-gray-400 flex-shrink-0" />
                            <div>
                              <span className="text-sm font-medium text-gray-900">
                                {user.organization.plan?.name || "Sin plan"}
                              </span>
                              {user.organization.plan &&
                                user.organization.plan.price > 0 && (
                                  <span className="text-xs text-gray-500 ml-1">
                                    ({user.organization.plan.price}€)
                                  </span>
                                )}
                            </div>
                          </div>
                        </td>
                        <td className="px-4 py-2.5">
                          {getRoleBadge(user.role)}
                        </td>
                        <td className="px-4 py-2.5">
                          <span
                            className={`px-2 py-0.5 text-xs font-medium rounded-full ${user.isActive ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}
                          >
                            {user.isActive ? "Activo" : "Inactivo"}
                          </span>
                        </td>
                        <td className="px-4 py-2.5">
                          <button
                            onClick={() => openNotesModal(user)}
                            title={
                              user.adminNotes
                                ? user.adminNotes
                                : "Añadir anotación"
                            }
                            className="flex items-center gap-1.5 group"
                          >
                            {user.adminNotes ? (
                              <>
                                <CheckCircle2 className="h-4 w-4 text-amber-500 flex-shrink-0" />
                                <span className="text-xs text-gray-600 max-w-[130px] truncate group-hover:text-primary-600">
                                  {user.adminNotes}
                                </span>
                              </>
                            ) : (
                              <span className="flex items-center gap-1 text-xs text-gray-400 hover:text-primary-600">
                                <StickyNote className="h-4 w-4" />
                                Añadir nota
                              </span>
                            )}
                          </button>
                        </td>
                        <td className="px-4 py-2.5 text-xs whitespace-nowrap">
                          {(() => {
                            const origen = origenDelRegistro(
                              user.organization?.adClickAttribution ?? null,
                            );
                            return (
                              <>
                                <span
                                  className={
                                    origen.texto === "Sin datos"
                                      ? "text-gray-400"
                                      : "text-gray-900 font-medium"
                                  }
                                >
                                  {origen.texto}
                                </span>
                                {origen.detalle && (
                                  <span className="block text-[11px] text-gray-400 truncate max-w-[180px]">
                                    {origen.detalle}
                                  </span>
                                )}
                              </>
                            );
                          })()}
                        </td>
                        <td className="px-4 py-2.5 text-xs text-gray-500 whitespace-nowrap">
                          {formatDate(user.createdAt)}
                        </td>
                        <td className="px-4 py-2.5 text-xs text-gray-500 whitespace-nowrap">
                          {formatDate(user.lastLoginAt)}
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          <div className="relative">
                            <button
                              onClick={() =>
                                setActiveDropdown(
                                  activeDropdown === user.id ? null : user.id,
                                )
                              }
                              className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded"
                              disabled={user.role === "owner"}
                            >
                              <MoreVertical size={16} />
                            </button>
                            {activeDropdown === user.id &&
                              user.role !== "owner" && (
                                <div className="absolute right-0 mt-1 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-10">
                                  {user.role === "admin" && user.isActive && (
                                    <button
                                      onClick={() => handleImpersonate(user)}
                                      disabled={
                                        impersonatingUserId === user.id
                                      }
                                      className="w-full px-4 py-2 text-left text-sm text-primary-700 hover:bg-primary-50 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                      <LogIn size={14} />
                                      {impersonatingUserId === user.id
                                        ? "Accediendo..."
                                        : "Acceder como"}
                                    </button>
                                  )}
                                  <button
                                    onClick={() => openEditModal(user)}
                                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                  >
                                    <Edit2 size={14} />
                                    Editar usuario
                                  </button>
                                  <button
                                    onClick={() => openNotesModal(user)}
                                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                  >
                                    <StickyNote size={14} />
                                    Anotaciones
                                  </button>
                                  <button
                                    onClick={() => handleToggleStatus(user)}
                                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                  >
                                    {user.isActive ? (
                                      <UserX size={14} />
                                    ) : (
                                      <UserCheck size={14} />
                                    )}
                                    {user.isActive ? "Desactivar" : "Activar"}
                                  </button>
                                  {!user.isActive && (
                                    <button
                                      onClick={() => openDeleteModal(user)}
                                      className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                                    >
                                      <Trash2 size={14} />
                                      Eliminar permanentemente
                                    </button>
                                  )}
                                </div>
                              )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Paginación fija al fondo */}
            {pagination.totalPages > 1 && (
              <div className="flex-shrink-0 flex items-center justify-between border-t border-gray-200 px-4 py-2.5">
                <div className="text-xs text-gray-500">
                  Mostrando {(pagination.page - 1) * pagination.limit + 1}–
                  {Math.min(
                    pagination.page * pagination.limit,
                    pagination.total,
                  )}{" "}
                  de {pagination.total}
                </div>
                <div className="flex space-x-2">
                  <Button
                    variant="secondary"
                    onClick={() =>
                      setPagination((prev) => ({
                        ...prev,
                        page: prev.page - 1,
                      }))
                    }
                    disabled={pagination.page === 1}
                  >
                    Anterior
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() =>
                      setPagination((prev) => ({
                        ...prev,
                        page: prev.page + 1,
                      }))
                    }
                    disabled={pagination.page === pagination.totalPages}
                  >
                    Siguiente
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal de edición de usuario */}
      {showEditModal && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6 border-b border-gray-200">
              <h3 className="text-lg font-semibold text-gray-900">
                Editar Usuario
              </h3>
              <p className="text-sm text-gray-500 mt-1">{selectedUser.email}</p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre
                </label>
                <input
                  type="text"
                  value={editFormData.firstName}
                  onChange={(e) =>
                    setEditFormData({
                      ...editFormData,
                      firstName: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Apellido
                </label>
                <input
                  type="text"
                  value={editFormData.lastName}
                  onChange={(e) =>
                    setEditFormData({
                      ...editFormData,
                      lastName: e.target.value,
                    })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Rol
                </label>
                <select
                  value={editFormData.role}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, role: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="staff">Trabajador</option>
                  <option value="admin">Hostelero</option>
                  <option value="owner">Superadmin</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Plan de la Organización
                </label>
                <select
                  value={editFormData.planId}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, planId: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                >
                  <option value="">Sin plan asignado</option>
                  {plans.map((plan) => (
                    <option key={plan.id} value={plan.id}>
                      {plan.name} - {plan.price}€/mes
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Cambiar el plan afectará a toda la organización:{" "}
                  {selectedUser?.organization.name}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Anotaciones internas
                </label>
                <textarea
                  value={editFormData.adminNotes}
                  onChange={(e) =>
                    setEditFormData({
                      ...editFormData,
                      adminNotes: e.target.value,
                    })
                  }
                  rows={3}
                  placeholder="Ej: Llamado el 10/05, interesado en plan Pro..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm resize-none"
                />
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowEditModal(false);
                  setSelectedUser(null);
                }}
                disabled={isUpdating}
              >
                Cancelar
              </Button>
              <Button
                variant="primary"
                onClick={handleUpdateUser}
                disabled={isUpdating}
              >
                {isUpdating ? "Guardando..." : "Guardar cambios"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de notas rápidas */}
      {showNotesModal && notesUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <StickyNote className="h-5 w-5 text-amber-500" />
                <h3 className="text-lg font-semibold text-gray-900">
                  Anotaciones
                </h3>
              </div>
              <p className="text-sm text-gray-500 mt-1">
                {notesUser.firstName} {notesUser.lastName} · {notesUser.email}
              </p>
              {notesUser.phone && (
                <div className="flex items-center gap-3 mt-1">
                  <a
                    href={`tel:${notesUser.phone}`}
                    className="flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-800"
                  >
                    <Phone className="h-4 w-4" />
                    {notesUser.phone}
                  </a>
                  {toWhatsAppNumber(notesUser.phone) && (
                    <a
                      href={`https://wa.me/${toWhatsAppNumber(notesUser.phone)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      title="Abrir chat de WhatsApp"
                      className="flex items-center gap-1.5 text-sm text-green-600 hover:text-green-700"
                    >
                      <MessageCircle className="h-4 w-4" />
                      WhatsApp
                    </a>
                  )}
                </div>
              )}
            </div>
            <div className="p-6">
              <textarea
                value={notesText}
                onChange={(e) => setNotesText(e.target.value)}
                rows={5}
                autoFocus
                placeholder="Ej: Llamado el 10/05 — interesado en plan Pro. Volver a llamar en junio."
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm resize-none"
              />
              <p className="text-xs text-gray-400 mt-1">
                Estas notas solo son visibles para el superadmin.
              </p>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowNotesModal(false);
                  setNotesUser(null);
                }}
                disabled={isSavingNotes}
              >
                Cancelar
              </Button>
              <Button
                variant="primary"
                onClick={handleSaveNotes}
                disabled={isSavingNotes}
              >
                {isSavingNotes ? "Guardando..." : "Guardar anotación"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de confirmación de eliminación */}
      {showDeleteModal && userToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="h-10 w-10 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900">
                  Eliminar usuario
                </h3>
              </div>
              <p className="text-sm text-gray-600 mb-2">
                ¿Estás seguro de que quieres eliminar permanentemente al usuario{" "}
                <strong>
                  {userToDelete.firstName} {userToDelete.lastName}
                </strong>{" "}
                ({userToDelete.email})?
              </p>
              <p className="text-sm text-red-600 font-medium">
                Esta acción no se puede deshacer. Se eliminarán todos sus datos
                asociados.
              </p>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end gap-3">
              <Button
                variant="secondary"
                onClick={() => {
                  setShowDeleteModal(false);
                  setUserToDelete(null);
                }}
                disabled={isDeleting}
              >
                Cancelar
              </Button>
              <button
                onClick={handleDeleteUser}
                disabled={isDeleting}
                className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDeleting ? "Eliminando..." : "Eliminar permanentemente"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
